import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../hooks/useStore'
import tts from '../../../../lib/TTS'
import {
  addToWatchList,
  getAllWatchlist,
  getWatchlist,
  removeFromWatchList,
} from '../../../../actions/contentActions'
import { useErrorModal } from '../../../../screens/VideoDetailScreen/context/ErrorModalProvider'
import { triggerSentryError } from '../../../../../../shared/helpers'
import { WatchlistContent } from '../../buttons/types'

const useManageWatchlist = () => {
  const { setErrorModalVisible, setErrorModalNotVisible, setErrorModalProps } =
    useErrorModal()
  const [inWatchlist, setInWatchlist] = useState(false)
  const { videoDetails } = useAppSelector((state) => state.content)
  const device = useAppSelector((state) => state.device)
  const personalization = useAppSelector((state) => state.personalization)

  useEffect(() => {
    if (personalization.watchlist === null) {
      fetchWatchlist()
    } else {
      inWatchlistCheck()
    }
  }, [])

  useEffect(() => {
    if (personalization.watchlist !== null) {
      inWatchlistCheck()
    }
  }, [personalization.watchlist])

  const inWatchlistCheck = () => {
    const contentWatchlisted = !!(
      personalization.watchlist &&
      personalization.watchlist.find(
        (contentInWatchlist: WatchlistContent) =>
          contentInWatchlist.cid === videoDetails.videoId
      )
    )
    if (contentWatchlisted || personalization.watchlistFullyLoaded) {
      setInWatchlist(contentWatchlisted)
    } else {
      fetchAllWatchlist()
    }
  }

  const fetchAllWatchlist = async () => {
    try {
      await getAllWatchlist(device.UID)
    } catch (error) {
      console.error('Error fetching all watchlist:', error)
    }
  }

  const fetchWatchlist = async () => {
    try {
      await getWatchlist(device.UID)
    } catch (error) {
      console.error('Error fetching watchlist:', error)
      return
    }
  }

  const addToWatchlist = async () => {
    try {
      const contentToWatchlist = {
        cid: videoDetails.videoId,
        images: {
          'asset-mezzanine-16x9': videoDetails.assetMezzanine,
        },
        contentType: videoDetails.contentType,
        item_type: 'video',
        flags: { is_mvod: videoDetails.is_mvod },
        duration: videoDetails.duration || 0,
        seconds_watched: videoDetails.seconds_watched || 0,
        slug: videoDetails.slug,
        title: videoDetails.episodeTitle,
        showTitle: videoDetails.showTitle,
        url: videoDetails.contentUrl,
        video_type: videoDetails.contentType,
        parent: videoDetails.parent || null,
      } as WatchlistContent
      await addToWatchList(contentToWatchlist)
      setInWatchlist(true)
      tts.addTextToRead('Added to my list')
    } catch (error) {
      console.error('Error adding to watchlist:', error)
      toggleErrorModal('There was an error adding this video to your watchlist')
      sendEventToSentry(error, 'addToWatchList')
    }
  }

  const removeFromWatchlist = async () => {
    try {
      await removeFromWatchList(videoDetails.videoId)
      setInWatchlist(false)
      tts.addTextToRead('Removed from my list')
    } catch (error) {
      console.error('Error removing from watchlist:', error)
      toggleErrorModal(
        'There was an error removing this video from your watchlist'
      )
      sendEventToSentry(error, 'removeFromWatchList')
    }
  }

  const toggleErrorModal = (title: string) => {
    setErrorModalVisible()
    setErrorModalProps({
      title,
      buttons: [
        {
          label: 'Go Back',
          onEnterPress: () => setErrorModalNotVisible(),
          focusKey: 'goBackButton',
        },
      ],
      hideStationLogo: false,
      description: '',
      error: null,
    })
  }

  const sendEventToSentry = (error, callbackName: string) => {
    const videoInfo = `${videoDetails.showTitle} | ${videoDetails.episodeTitle} | ${videoDetails.videoId} | ${videoDetails.contentType}`
    const errorDescription = `Error ${
      error.status ? error.status : '(unknown)'
    } adding video ${videoInfo} to watchlist`

    triggerSentryError(
      callbackName,
      'useManageWatchlist hook',
      {
        errorMessage: error.message ?? 'Undefined error message (default)',
        errorDescription,
        errorLocation: 'useManageWatchlist',
      },
      true
    )
  }

  return {
    removeFromWatchlist,
    addToWatchlist,
    inWatchlist,
  }
}

export default useManageWatchlist
